import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  CISA_FUNCTION_TO_ZT,
  aggregateLevels,
  isFunctionMapped
} from '../config/cisaZtMapping';

/**
 * useTechStackSync
 *
 * Reads the customer's tech-stack state (from the ZT Architecture page) out of
 * localStorage and exposes derivation helpers for the CISA Maturity Model:
 *
 *   getDerivedLevel(functionCode)      → 'INITIAL' | 'ADVANCED' | etc. | null
 *   getContributingCapabilities(fnCode) → [{ capId, name, maturity, vendors }]
 *
 * Listens to the `storage` event so cross-tab edits in the Architecture page
 * propagate to an open CISA page in real time. Same-tab edits are picked up
 * via a small polling interval (localStorage events don't fire on the same
 * tab that wrote them) — this is cheap because we're just reading a JSON blob.
 */

const ZT_CACHE_PREFIX = 'zt_cache_';
const POLL_MS = 1500;

/** Mirrors the capability metadata authored on the ZT Architecture page. */
const ZT_CAPABILITY_NAMES = {
  // Identity
  idp: 'IdP',
  mfa: 'MFA – Phishing-Resistant FIDO2',
  iga: 'Identity Governance & Admin (IGA)',
  pam: 'Privileged Access Mgmt (PAM)',
  'machine-id': 'Machine / Service Identity',
  conditional: 'Risk-Based Conditional Access',

  // Network
  ztna: 'ZTNA – Replace VPN',
  microseg: 'Microsegmentation',
  dns: 'DNS Security',
  sdwan: 'SD-WAN / SASE',
  nta: 'Network Traffic Analysis (NTA)',
  lateral: 'East-West Lateral Movement Controls'
};

function readZtCache(departmentId) {
  if (!departmentId) return null;
  try {
    const raw = localStorage.getItem(`${ZT_CACHE_PREFIX}${departmentId}`);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed?.data?.capabilities || null;
  } catch {
    return null;
  }
}

export function useTechStackSync(departmentId) {
  const [capabilities, setCapabilities] = useState(() => readZtCache(departmentId));

  // Re-read when departmentId changes
  useEffect(() => {
    setCapabilities(readZtCache(departmentId));
  }, [departmentId]);

  // Cross-tab sync: localStorage writes from the ZT Architecture page in
  // another tab fire a `storage` event here.
  useEffect(() => {
    if (!departmentId) return undefined;
    const key = `${ZT_CACHE_PREFIX}${departmentId}`;
    const handler = (e) => {
      if (e.key === key) {
        setCapabilities(readZtCache(departmentId));
      }
    };
    window.addEventListener('storage', handler);
    return () => window.removeEventListener('storage', handler);
  }, [departmentId]);

  // Same-tab sync: the storage event doesn't fire for writes from the same
  // tab. Poll the cache so a customer flipping between Architecture and CISA
  // tabs in the same window sees changes immediately.
  useEffect(() => {
    if (!departmentId) return undefined;
    const id = setInterval(() => {
      const next = readZtCache(departmentId);
      setCapabilities(prev => {
        // Cheap shallow check — if the JSON serialization differs, update.
        if (JSON.stringify(prev) !== JSON.stringify(next)) return next;
        return prev;
      });
    }, POLL_MS);
    return () => clearInterval(id);
  }, [departmentId]);

  /**
   * Get the {maturity, vendors} state of a single ZT capability for the
   * current customer, or null if the customer hasn't touched it.
   */
  const getCapabilityState = useCallback((capId) => {
    const cap = capabilities?.[capId];
    if (!cap) return null;
    return {
      capabilityId: capId,
      name: ZT_CAPABILITY_NAMES[capId] || capId,
      maturity: cap.maturity || null,
      vendors: Array.isArray(cap.vendors) ? cap.vendors : [],
      status: cap.status || null,
      notes: cap.notes || ''
    };
  }, [capabilities]);

  /**
   * Get every ZT capability that contributes to a CISA function, with the
   * customer's current state. Capabilities the customer hasn't touched are
   * still returned (with maturity: null, vendors: []) so the workshop can see
   * "you have nothing assigned to PAM yet" rather than silently omitting it.
   */
  const getContributingCapabilities = useCallback((functionCode) => {
    const capIds = CISA_FUNCTION_TO_ZT[functionCode] || [];
    return capIds.map(capId => {
      const state = getCapabilityState(capId);
      return state || {
        capabilityId: capId,
        name: ZT_CAPABILITY_NAMES[capId] || capId,
        maturity: null,
        vendors: [],
        status: null,
        notes: ''
      };
    });
  }, [getCapabilityState]);

  /**
   * Derive a CISA-function-level maturity from the customer's tech stack.
   * Returns null if the function has no mapping or no contributing capability
   * has data set yet.
   */
  const getDerivedLevel = useCallback((functionCode) => {
    if (!isFunctionMapped(functionCode)) return null;
    const contribs = getContributingCapabilities(functionCode);
    return aggregateLevels(contribs.map(c => c.maturity));
  }, [getContributingCapabilities]);

  /**
   * Flat list of unique vendors a customer has assigned to capabilities that
   * feed the given CISA function. Used for "current vendors" in the drawer.
   */
  const getVendorsForFunction = useCallback((functionCode) => {
    const contribs = getContributingCapabilities(functionCode);
    const set = new Set();
    for (const cap of contribs) {
      for (const v of (cap.vendors || [])) set.add(v);
    }
    return Array.from(set);
  }, [getContributingCapabilities]);

  const hasAnyTechStack = useMemo(
    () => !!capabilities && Object.keys(capabilities).length > 0,
    [capabilities]
  );

  return {
    hasAnyTechStack,
    getCapabilityState,
    getContributingCapabilities,
    getDerivedLevel,
    getVendorsForFunction
  };
}

export default useTechStackSync;
